import { defineStore } from "pinia";
import axios from "axios";
import { useNotification } from "@kyvg/vue3-notification";
import { AppStoreState, AppStoreActions, AppStoreGetters, Coupon, NotificationData } from "./storeTypes";

const { notify } = useNotification();

// --- Store ---
export const useAppStore = defineStore<
  "app",
  AppStoreState,
  AppStoreGetters,
  AppStoreActions
>("app", {
  state: (): AppStoreState => ({
    user: {},
    configs: false,
    user_authorization: {},
    notification: {},
    warning: {},
    modal: {},
    stripeLoaded: false,
    refresh_table: false,
    callback: {},
    new_version: false,
    notifications: [],
    unreadNotificationCount: null,
    ui: { popup_inside_modal: false, edit_custom_plan: false },
    selectedPlan: null,
    selectedColor: "#0d68b7",
    companyLogo: "",
    menuModules: null,
    themeData: {},
    taskSections: [],
    currentMenu: {},
    token: null,
    locations: [],
    departments: [],
    designations: [],
    employees: [],
    isChangedData: false,
    paginateData: { currentPage: 1 },
    addressData: { countryId: null, stateId: null },
    hrmCountries: [],
    hrmStates: [],
    hrmCities: [],
    coupon: { coupon_code: null, name: null },
    coupon_applied: false,
    coupon_banner_text: null,
    geoLocation: { latitude: null, longitude: null },
    exportData: {
      files: {},
      showExportProgress: false,
      newExportKey: ""
    },
    hrmThemeData: {},
    chatUserChannelName: {}
  }),
  actions: {
    setSelectedColor(color) {
      this.selectedColor = color;
      document.documentElement.style.setProperty("--app-store-selected-color", color);
    },
    closewindow(value) {
      this.callback = value;
    },
    userLogin(data) {
      this.user = data;
    },
    userLogout() {
      this.user = {};
      this.user_authorization = {};
    },
    subscribeAppVersionReleaseEvent() {
      window.Echo.channel(`app.version.release`).listen("AppVersionRelease", (e: any) => {
        this.new_version = e.version;
      });
    },
    setVersion() {
      this.new_version = false;
    },
    async getNotifications() {
      try {
        const queryData = { page: 1, perPage: 15 };
        const response = await axios.get(route("hrm.notifications"), { params: queryData });
        this.notifications = response.data.data;
        this.unreadNotificationCount = response.data.un_read_count;
      } catch (error: any) {
        console.error("Error fetching notifications:", error.message);
        return [];
      }
    },
    async getDefaultCoupon() {
      const response = await axios.get("/api/v1/default-coupon");
      let coupon = response.data.data.coupon;
      let coupon_banner_text = response.data.data.coupon_banner_text;

      if (this.user?.active_plan?.type !== "free" && response.data.data.coupon_only_for_free) {
        coupon = { name: null, coupon_code: null, discount_value: null };
        coupon_banner_text = null;
      }

      this.coupon = coupon;
      this.coupon_applied = !!coupon.coupon_code;
      this.coupon_banner_text = coupon_banner_text ?? null;
      return true;
    },
    async logout(type) {
      try {
        const response = await axios.post(route("logout"));
        return { success: true, message: response.data.message, type };
      } catch (error) {
        console.error("There was an error logging out:", error);
      }
    },
    setNotification(notification: NotificationData) {
      if (notification.heading && notification.subHeading) {
        notify({ title: notification.heading, text: notification.subHeading, type: notification.type });
        this.notification = notification;
      }
    },
    changeCouponCode(coupon_code: string) {
      if (this.coupon) this.coupon.coupon_code = coupon_code;
    },
    toggleCouponApplied() {
      this.coupon_applied = !this.coupon_applied;
    },
    applyCoupon(coupon: Coupon) {
      this.coupon = coupon;
      this.coupon_applied = true;
    },
    removeCoupon() {
      this.coupon = { coupon_code: null, name: null, percentage: null, text: null };
      this.coupon_applied = false;
    },
    setWarning(warning: Record<string, any>) {
      this.warning = warning;
    },
    getCountries() {
      axios.get(route("hrm.countries.index")).then((res) => (this.hrmCountries = res.data.data));
    },
    handleExportData(exportKey: string) {
      this.exportData.showExportProgress = true;
      const newFile = {
        uuid: exportKey,
        progress: 0,
        status: "pending",
        file: null,
      };
      this.exportData.files = {
        [exportKey]: newFile,
        ...(this.exportData?.files ? this.exportData.files : {}),
      };
      this.exportData.newExportKey = exportKey;
    },
    // Export Data Update
    updateExportFiles(uuid, data) {
      if (!uuid) return;
      this.exportData.files[uuid] = data;
    },
    async getModules() {
      try {
        const response = await axios.get(route("hrm.modules"), { params: { per_page: "all" } })
        this.menuModules = response.data.data;
      } catch (error) {
        console.log("Error fetching modules", error);
      }
    },
    async getThemeSettings() {
      try {
        const themeData = await axios.get(route('hrm.get.theme'));
        if (themeData.data?.data?.layout_card_type) {
          this.hrmThemeData = themeData.data.data;
        }
      } catch (err) {
        console.error(err);
      }
    }
  },
  getters: {
    unreadNotifications: (state) => state.unreadNotificationCount,
    recentNotifications: (state) => state.notifications.slice(0, 5),
    getCallback: (state) => state.callback,
    userCan: (state) => state.user_authorization.userCan,
    userUsed: (state) => state.user_authorization.userUsed,
    userAllow: (state) => state.user_authorization.userAllow,
    userActivePlan: (state) => state.user.active_plan,
    getUser: (state) => state.user,
    getcallback: (state) => state,
    getRefreshTable: (state) => state.refresh_table,
    getNewVersion: (state) => state.new_version,
    appliedCoupon: (state) => state.coupon,
    shouldCouponApply: (state) => state.coupon_applied,
  },
  //   strict: process.env.NODE_ENV !== "production",
});
